import {
  Paper, Table, Text, Badge, Group, Box, Flex, Grid, Card, Divider,
  Button, Select, TextInput, ActionIcon, Checkbox, Modal, Stack, NumberInput,
} from '@mantine/core';
import { useState, useMemo, useEffect } from 'react';
import {
  IconFilter, IconSearch, IconCheck, IconX, IconChevronLeft, IconChevronRight,
  IconPlus, IconEdit, IconTrash,
} from '@tabler/icons-react';
import { WEEKDAYS_FULL, getMonthName } from './dashboardData';
import api from './api';

// ============================================================
// CONSTANTES
// ============================================================

const MESES = [
  { value: '1', label: 'ENERO' }, { value: '2', label: 'FEBRERO' },
  { value: '3', label: 'MARZO' }, { value: '4', label: 'ABRIL' },
  { value: '5', label: 'MAYO' }, { value: '6', label: 'JUNIO' },
  { value: '7', label: 'JULIO' }, { value: '8', label: 'AGOSTO' },
  { value: '9', label: 'SEPTIEMBRE' }, { value: '10', label: 'OCTUBRE' },
  { value: '11', label: 'NOVIEMBRE' }, { value: '12', label: 'DICIEMBRE' },
];

const getYearOptions = () => {
  const y = new Date().getFullYear();
  return Array.from({ length: 7 }, (_, i) => ({ value: String(y - 3 + i), label: String(y - 3 + i) }));
};

// Servicios por defecto (mientras no exista el endpoint)
const SERVICIOS_DEFAULT = [
  { value: 'Express', label: 'Express' },
  { value: 'Estándar', label: 'Estándar' },
  { value: 'Perecedero', label: 'Perecedero' },
  { value: 'Farmacéutico', label: 'Farmacéutico' },
  { value: 'E-Commerce', label: 'E-Commerce' },
  { value: 'Paquetería', label: 'Paquetería' },
  { value: 'Carga General', label: 'Carga General' },
];

// ============================================================
// FÓRMULAS
// ============================================================

function calcCapRem(vuelo) {
  const capKg = (vuelo.CapTons ?? 0) * 1000;
  const capM3 = vuelo.CapM3 ?? 0;
  let sumKg = 0, sumM3 = 0;
  (vuelo.clientes || []).forEach((c) => {
    sumKg += Number(c.kg ?? 0);
    sumM3 += Number(c.m3 ?? 0);
  });
  return { remKg: Math.round(capKg - sumKg), remM3: Math.round((capM3 - sumM3) * 100) / 100 };
}

function calcFOCv2(vuelo) {
  const capKg = (vuelo.CapTons ?? 0) * 1000;
  const capM3 = vuelo.CapM3 ?? 0;
  const focKg = {}, focVol = {};
  WEEKDAYS_FULL.forEach((dia) => {
    let sumKg = 0, sumM3 = 0;
    (vuelo.clientes || []).forEach((c) => {
      if (c.dias?.[dia]) {
        sumKg += Number(c.kg ?? 0);
        sumM3 += Number(c.m3 ?? 0);
      }
    });
    focKg[dia] = capKg > 0 ? Math.round((sumKg / capKg) * 100) + '%' : '0%';
    focVol[dia] = capM3 > 0 ? Math.round((sumM3 / capM3) * 100) + '%' : '0%';
  });
  return { focKg, focVol };
}

const semaforo = (val) => {
  const n = parseFloat(val || '0');
  if (n >= 70) return { bg: '#e8f5e9', c: '#1b5e20' };
  if (n >= 40) return { bg: '#fff8e1', c: '#e65100' };
  if (n > 0) return { bg: '#fce4ec', c: '#b71c1c' };
  return { bg: '#f5f5f5', c: '#999' };
};

// ============================================================
// DATA DUMMY (será reemplazado por API)
// ============================================================

// Catálogo de clientes disponibles (se carga del endpoint)
const CATALOGO_CLIENTES_FALLBACK = [];

// ============================================================
// COMPONENT
// ============================================================

export default function DashboardV2() {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [anio, setAnio] = useState(now.getFullYear());

  // Vuelos
  const [vuelosState, setVuelosState] = useState([]);
  const [selectedVueloId, setSelectedVueloId] = useState(null);
  const [focMesAnterior, setFocMesAnterior] = useState({});
  const [loading, setLoading] = useState(false);

  // Catálogo clientes
  const [catalogoClientes, setCatalogoClientes] = useState([]);
  // Catálogo servicios
  const [catalogoServicios, setCatalogoServicios] = useState([]);

  // Filtros
  const [search, setSearch] = useState('');
  const [rutaFilter, setRutaFilter] = useState(null);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCliente, setModalCliente] = useState(null);
  const [modalForm, setModalForm] = useState({ clienteId: null, kg: 0, m3: 0, prioridad: 1, servicio: '', dias: { L: true, M: true, X: true, J: true, V: true, S: false, D: false } });
  const [saving, setSaving] = useState(false);

  // ============================================================
  // CARGAR DATOS
  // ============================================================

  const loadVuelos = async (m, a) => {
    setLoading(true);
    try {
      const res = await api.get(`/planeacion/v2/resumen?mes=${m}&anio=${a}`);
      const vuelos = Array.isArray(res.data?.vuelos) ? res.data.vuelos : [];
      setVuelosState(vuelos);
      // Mantener selección si existe
      if (selectedVueloId && !vuelos.find((v) => v.ID === selectedVueloId)) {
        setSelectedVueloId(vuelos[0]?.ID || null);
      }
    } catch (err) {
      console.error('[V2] Error cargando resumen:', err.message);
      setVuelosState([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFocAnterior = async (m, a) => {
    const prevM = m === 1 ? 12 : m - 1;
    const prevA = m === 1 ? a - 1 : a;
    try {
      const res = await api.get(`/foc?mes=${prevM}&anio=${prevA}&limit=5000`);
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      const dayMap = { 'Lunes': 'L', 'Martes': 'M', 'Miércoles': 'X', 'Miercoles': 'X', 'Jueves': 'J', 'Viernes': 'V', 'Sábado': 'S', 'Sabado': 'S', 'Domingo': 'D' };
      const focMap = {};
      data.forEach((row) => {
        if (!row.ID) return;
        const converted = {};
        Object.entries(row).forEach(([key, val]) => { const l = dayMap[key]; if (l) converted[l] = typeof val === 'string' ? val : Math.round(Number(val || 0)) + '%'; });
        focMap[row.ID] = converted;
      });
      setFocMesAnterior(focMap);
    } catch (err) {
      console.warn('[V2] FOC anterior error:', err.message);
    }
  };

  const loadCatalogo = async () => {
    try {
      const res = await api.get('/planeacion/clientes');
      let lista = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data?.clientes) ? res.data.clientes : [];
      setCatalogoClientes(lista.map((c) => ({ id: c.id_cliente ?? c.idCliente ?? c.id ?? c.ID, nombre: c.nombre ?? c.nombre_cliente ?? c.Nombre ?? '' })));
    } catch (err) {
      console.warn('[V2] Catálogo error:', err.message);
      setCatalogoClientes(CATALOGO_CLIENTES_FALLBACK);
    }
  };

  const loadServicios = async () => {
    try {
      const res = await api.get('/planeacion/v2/servicios');
      const data = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
      if (data.length > 0) {
        setCatalogoServicios(data.map((s) => ({ value: String(s.nombre ?? s.id ?? s), label: String(s.nombre ?? s) })));
      } else {
        setCatalogoServicios(SERVICIOS_DEFAULT);
      }
    } catch (err) {
      console.warn('[V2] Servicios error:', err.message);
      setCatalogoServicios(SERVICIOS_DEFAULT);
    }
  };

  useEffect(() => { loadCatalogo(); loadServicios(); }, []);
  useEffect(() => { loadVuelos(mes, anio); loadFocAnterior(mes, anio); }, [mes, anio]);

  // Filtrado
  const filteredVuelos = useMemo(() => {
    let data = [...vuelosState];
    if (search) {
      const s = search.toLowerCase();
      data = data.filter((v) => v.ID.toLowerCase().includes(s) || v.Ruta.toLowerCase().includes(s));
    }
    if (rutaFilter) data = data.filter((v) => v.Ruta === rutaFilter);
    return data;
  }, [vuelosState, search, rutaFilter]);

  const rutaOptions = useMemo(() => {
    const set = new Set(vuelosState.map((v) => v.Ruta));
    return [...set].sort().map((r) => ({ value: r, label: r }));
  }, [vuelosState]);

  // Vuelo activo
  const activeVuelo = useMemo(() => filteredVuelos.find((v) => v.ID === selectedVueloId) || null, [filteredVuelos, selectedVueloId]);

  // Cálculos
  const capRem = useMemo(() => activeVuelo ? calcCapRem(activeVuelo) : { remKg: 0, remM3: 0 }, [activeVuelo]);
  const foc = useMemo(() => activeVuelo ? calcFOCv2(activeVuelo) : { focKg: {}, focVol: {} }, [activeVuelo]);
  const focAnt = useMemo(() => activeVuelo ? (focMesAnterior[activeVuelo.ID] || null) : null, [activeVuelo, focMesAnterior]);

  // Navegación mes
  const prevMonth = () => { setMes(mes === 1 ? 12 : mes - 1); setAnio(mes === 1 ? anio - 1 : anio); };
  const nextMonth = () => { setMes(mes === 12 ? 1 : mes + 1); setAnio(mes === 12 ? anio + 1 : anio); };

  // ============================================================
  // MODAL - Asignar / Editar cliente
  // ============================================================

  const openNewCliente = () => {
    setModalCliente(null);
    const maxPrioridad = Math.max(0, ...(activeVuelo?.clientes || []).map((c) => c.prioridad || 0));
    setModalForm({ clienteId: null, kg: 0, m3: 0, prioridad: maxPrioridad + 1, servicio: '', comentario: '', dias: { L: true, M: true, X: true, J: true, V: true, S: false, D: false } });
    setModalOpen(true);
  };

  const openEditCliente = (cliente) => {
    setModalCliente(cliente);
    setModalForm({ clienteId: String(cliente.id), kg: cliente.kg, m3: cliente.m3, prioridad: cliente.prioridad || 1, servicio: cliente.servicio || '', comentario: cliente.comentario || '', dias: { ...cliente.dias } });
    setModalOpen(true);
  };

  const saveModal = async () => {
    if (!activeVuelo || !modalForm.clienteId) return;
    setSaving(true);
    try {
      if (modalCliente && modalCliente.id_asignacion) {
        // EDITAR (PUT) — tiene id_asignacion
        const payload = {
          id_asignacion: modalCliente.id_asignacion,
          kg: Number(modalForm.kg),
          m3: Number(modalForm.m3),
          prioridad: Number(modalForm.prioridad),
          servicio: modalForm.servicio || '',
          comentario: modalForm.comentario || '',
          dias: modalForm.dias,
        };
        console.log('[V2] PUT asignacion:', payload);
        await api.put('/planeacion/v2/asignacion', payload);
      } else {
        // CREAR NUEVO (POST)
        const payload = {
          ruta: activeVuelo.Ruta,
          vuelo: Number(activeVuelo.Vuelo),
          id_cliente: Number(modalForm.clienteId),
          mes,
          anio,
          kg: Number(modalForm.kg),
          m3: Number(modalForm.m3),
          prioridad: Number(modalForm.prioridad),
          servicio: modalForm.servicio || '',
          comentario: modalForm.comentario || '',
          dias: modalForm.dias,
        };
        console.log('[V2] POST asignacion:', payload);
        await api.post('/planeacion/v2/asignacion', payload);
      }
      setModalOpen(false);
      await loadVuelos(mes, anio);
    } catch (err) {
      console.error('[V2] Error guardando:', err?.response?.data || err.message);
    } finally {
      setSaving(false);
    }
  };

  const removeCliente = async (clienteId) => {
    if (!activeVuelo) return;
    try {
      await api.post('/planeacion/v2/asignacion/eliminar', {
        ruta: activeVuelo.Ruta,
        vuelo: Number(activeVuelo.Vuelo),
        id_cliente: Number(clienteId),
        mes,
        anio,
      });
      await loadVuelos(mes, anio);
    } catch (err) {
      console.error('[V2] Error eliminando:', err?.response?.data || err.message);
    }
  };

  // Toggle check inline → guarda al backend
  const toggleCheck = async (clienteId, dia) => {
    if (!activeVuelo) return;
    const cliente = activeVuelo.clientes.find((c) => c.id === clienteId);
    if (!cliente) return;
    const newDias = { ...cliente.dias, [dia]: !cliente.dias[dia] };

    // Optimistic update
    setVuelosState((prev) => prev.map((v) => {
      if (v.ID !== selectedVueloId) return v;
      return { ...v, clientes: v.clientes.map((c) => c.id !== clienteId ? c : { ...c, dias: newDias }) };
    }));

    // Persist
    try {
      if (cliente.id_asignacion) {
        await api.put('/planeacion/v2/asignacion', {
          id_asignacion: cliente.id_asignacion,
          kg: Number(cliente.kg),
          m3: Number(cliente.m3),
          prioridad: Number(cliente.prioridad),
          servicio: cliente.servicio || '',
          comentario: cliente.comentario || '',
          dias: newDias,
        });
      } else {
        await api.post('/planeacion/v2/asignacion', {
          ruta: activeVuelo.Ruta,
          vuelo: Number(activeVuelo.Vuelo),
          id_cliente: Number(clienteId),
          mes,
          anio,
          kg: Number(cliente.kg),
          m3: Number(cliente.m3),
          prioridad: Number(cliente.prioridad),
          servicio: cliente.servicio || '',
          comentario: cliente.comentario || '',
          dias: newDias,
        });
      }
    } catch (err) {
      console.error('[V2] Error toggle:', err.message);
      // Revert on error
      await loadVuelos(mes, anio);
    }
  };

  // Clientes disponibles para el select (permitir duplicados)
  const clienteSelectOptions = useMemo(() => {
    return catalogoClientes.map((c) => ({ value: String(c.id), label: c.nombre }));
  }, [catalogoClientes]);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <>
      {/* HEADER */}
      <Flex justify="space-between" align="center" mb="md" wrap="wrap" gap="md">
        <Text fw={700} size="xl" c="#020C41">Planeación Comercial v2</Text>
        <Group gap="xs">
          <ActionIcon size="sm" variant="light" color="#030D75" onClick={prevMonth}><IconChevronLeft size={14} /></ActionIcon>
          <Select value={String(mes)} onChange={(v) => v && setMes(parseInt(v, 10))} data={MESES} size="xs" allowDeselect={false} style={{ width: 135 }} />
          <Select value={String(anio)} onChange={(v) => v && setAnio(parseInt(v, 10))} data={getYearOptions()} size="xs" allowDeselect={false} style={{ width: 90 }} />
          <ActionIcon size="sm" variant="light" color="#030D75" onClick={nextMonth}><IconChevronRight size={14} /></ActionIcon>
        </Group>
        <Text size="xs" c="dimmed">{getMonthName(mes).toUpperCase()} {anio}</Text>
      </Flex>

      {/* FILTROS */}
      <Paper shadow="xs" p="md" mb="md" radius="md">
        <Flex gap="md" align="end" wrap="wrap">
          <TextInput label="Buscar" placeholder="ID o Ruta..." leftSection={<IconSearch size={16} />} value={search} onChange={(e) => setSearch(e.currentTarget.value)} style={{ flex: 1, minWidth: 200 }} />
          <Select label="Ruta" placeholder="Todas" data={rutaOptions} value={rutaFilter} onChange={setRutaFilter} clearable searchable leftSection={<IconFilter size={16} />} style={{ minWidth: 160 }} />
        </Flex>
      </Paper>

      <Grid gutter="md">
        {/* LISTA VUELOS */}
        <Grid.Col span={3}>
          <Paper shadow="xs" radius="md" style={{ overflow: 'hidden' }}>
            <Box p="sm" style={{ backgroundColor: '#030D75' }}>
              <Text size="sm" fw={600} c="white">Vuelos ({filteredVuelos.length})</Text>
            </Box>
            <Box style={{ maxHeight: 550, overflowY: 'auto' }}>
              {filteredVuelos.map((v) => (
                <Box key={v.ID} onClick={() => setSelectedVueloId(v.ID)}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #eee', backgroundColor: selectedVueloId === v.ID ? 'rgba(250,0,115,0.08)' : undefined, borderLeft: selectedVueloId === v.ID ? '3px solid #FA0073' : '3px solid transparent' }}>
                  <Text size="sm" fw={600} c="#020C41">{v.ID}</Text>
                  <Text size="xs" c="dimmed">{v.EQ} · {v.ETD} · {v.clientes.length} cl.</Text>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid.Col>

        {/* DETALLE */}
        <Grid.Col span={9}>
          {activeVuelo ? (
            <Paper shadow="xs" radius="md" p="md">
              {/* Info vuelo */}
              <Flex justify="space-between" align="center" mb="sm">
                <Group gap="sm">
                  <Text size="lg" fw={700} c="#020C41">{activeVuelo.ID}</Text>
                  <Badge color="am-blue" variant="light">{activeVuelo.Ruta}</Badge>
                  <Badge color="am-pink" variant="light">VLO {activeVuelo.Vuelo}</Badge>
                </Group>
                <Text size="xs" c="dimmed">{activeVuelo.EQ} · {activeVuelo.Frecuencias}</Text>
              </Flex>

              {/* Capacidades */}
              <Card withBorder radius="sm" p="xs" mb="sm" style={{ backgroundColor: '#F2F2F0' }}>
                <Group gap="xl">
                  <Box><Text size="xs" c="dimmed">Cap (T)</Text><Text size="sm" fw={600}>{activeVuelo.CapTons}</Text></Box>
                  <Box><Text size="xs" c="dimmed">Cap (m³)</Text><Text size="sm" fw={600}>{activeVuelo.CapM3}</Text></Box>
                  <Box><Text size="xs" c="dimmed">Rem Kg</Text><Text size="sm" fw={600} c={capRem.remKg < 0 ? '#FA0073' : '#1b5e20'}>{capRem.remKg}</Text></Box>
                  <Box><Text size="xs" c="dimmed">Rem m³</Text><Text size="sm" fw={600} c={capRem.remM3 < 0 ? '#FA0073' : '#1b5e20'}>{capRem.remM3}</Text></Box>
                  <Box><Text size="xs" c="dimmed">ETD → ETA</Text><Text size="sm" fw={600}>{activeVuelo.ETD} → {activeVuelo.ETA}</Text></Box>
                </Group>
              </Card>

              {/* FOC Comparativa: Actual vs Mes Anterior */}
              <Box mb="sm" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.65rem', border: '1px solid #e0e0e0', borderRadius: 6 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#030D75' }}>
                      <th style={{ color: 'white', padding: '3px 6px', textAlign: 'left', fontSize: '0.6rem' }}></th>
                      {WEEKDAYS_FULL.map((d) => <th key={d} style={{ color: 'white', padding: '3px 4px', textAlign: 'center', fontSize: '0.6rem', minWidth: 36 }}>{d}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {/* FOC Kg actual */}
                    <tr>
                      <td style={{ padding: '2px 6px', fontWeight: 700, fontSize: '0.6rem', color: '#FA0073', borderRight: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>Kg actual</td>
                      {WEEKDAYS_FULL.map((d) => { const s = semaforo(foc.focKg[d]); return <td key={d} style={{ backgroundColor: s.bg, color: s.c, fontWeight: 600, fontSize: '0.65rem', padding: '2px 4px', textAlign: 'center' }}>{foc.focKg[d]}</td>; })}
                    </tr>
                    {/* FOC Kg mes anterior */}
                    {focAnt && (
                      <tr style={{ opacity: 0.6 }}>
                        <td style={{ padding: '2px 6px', fontWeight: 500, fontSize: '0.6rem', color: '#FA0073', borderRight: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>Kg ant.</td>
                        {WEEKDAYS_FULL.map((d) => { const s = semaforo(focAnt[d]); return <td key={d} style={{ backgroundColor: s.bg, color: s.c, fontWeight: 500, fontSize: '0.65rem', padding: '2px 4px', textAlign: 'center' }}>{focAnt[d] || '0%'}</td>; })}
                      </tr>
                    )}
                    {/* FOC Vol actual */}
                    <tr style={{ borderTop: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '2px 6px', fontWeight: 700, fontSize: '0.6rem', color: '#030D75', borderRight: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>Vol actual</td>
                      {WEEKDAYS_FULL.map((d) => { const s = semaforo(foc.focVol[d]); return <td key={d} style={{ backgroundColor: s.bg, color: s.c, fontWeight: 600, fontSize: '0.65rem', padding: '2px 4px', textAlign: 'center' }}>{foc.focVol[d]}</td>; })}
                    </tr>
                  </tbody>
                </table>
              </Box>

              <Divider mb="sm" />

              {/* Clientes Header */}
              <Flex justify="space-between" align="center" mb="sm">
                <Text size="xs" fw={600} c="#020C41">Clientes ({(activeVuelo.clientes || []).length}) — {getMonthName(mes).toUpperCase()} {anio}</Text>
                <Button size="compact-xs" color="#FA0073" leftSection={<IconPlus size={12} />} onClick={openNewCliente}>Asignar cliente</Button>
              </Flex>

              {/* TABLA CLIENTES */}
              <Box style={{ overflowX: 'auto' }}>
                <Table withTableBorder withColumnBorders horizontalSpacing={4} verticalSpacing={4} style={{ fontSize: '0.75rem', textAlign: 'center' }}>
                  <Table.Thead style={{ backgroundColor: '#030D75' }}>
                    <Table.Tr>
                      <Table.Th style={{ color: 'white', textAlign: 'center', padding: '4px 6px', minWidth: 30 }}>P</Table.Th>
                      <Table.Th style={{ color: 'white', textAlign: 'left', padding: '4px 8px', minWidth: 140 }}>Cliente</Table.Th>
                      <Table.Th style={{ color: 'white', textAlign: 'center', padding: '4px 6px', minWidth: 80 }}>Servicio</Table.Th>
                      <Table.Th style={{ color: 'white', textAlign: 'center', padding: '4px 6px', minWidth: 50 }}>Kg</Table.Th>
                      <Table.Th style={{ color: 'white', textAlign: 'center', padding: '4px 6px', minWidth: 50 }}>m³</Table.Th>
                      {WEEKDAYS_FULL.map((d) => <Table.Th key={d} style={{ color: 'white', textAlign: 'center', padding: '4px 6px', minWidth: 38 }}>{d}</Table.Th>)}
                      <Table.Th style={{ color: 'white', textAlign: 'center', padding: '4px 6px', minWidth: 50 }}></Table.Th>
                      <Table.Th style={{ color: 'white', textAlign: 'left', padding: '4px 6px', minWidth: 100 }}>Comentario</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {(activeVuelo.clientes || []).sort((a, b) => a.prioridad - b.prioridad).map((cliente) => (
                      <Table.Tr key={cliente.id}>
                        <Table.Td style={{ textAlign: 'center' }}><Badge size="xs" color="#030D75" variant="filled" circle>{cliente.prioridad}</Badge></Table.Td>
                        <Table.Td style={{ textAlign: 'left', fontWeight: 600, color: '#020C41' }}>{cliente.nombre}</Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}><Text size="xs" c="dimmed">{cliente.servicio || '—'}</Text></Table.Td>
                        <Table.Td style={{ textAlign: 'center', fontWeight: 600 }}>{cliente.kg}</Table.Td>
                        <Table.Td style={{ textAlign: 'center', fontWeight: 600 }}>{cliente.m3}</Table.Td>
                        {WEEKDAYS_FULL.map((dia) => (
                          <Table.Td key={dia} style={{ textAlign: 'center', backgroundColor: cliente.dias[dia] ? 'rgba(46,125,50,0.06)' : 'rgba(250,0,115,0.04)' }}>
                            <Checkbox size="xs" checked={cliente.dias[dia] ?? false} onChange={() => toggleCheck(cliente.id, dia)} color="#FA0073" styles={{ root: { display: 'flex', justifyContent: 'center' } }} />
                          </Table.Td>
                        ))}
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Group gap={2} justify="center">
                            <ActionIcon size="xs" variant="subtle" color="#030D75" onClick={() => openEditCliente(cliente)}><IconEdit size={12} /></ActionIcon>
                            <ActionIcon size="xs" variant="subtle" color="red" onClick={() => removeCliente(cliente.id)}><IconTrash size={12} /></ActionIcon>
                          </Group>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'left', maxWidth: 150 }}>
                          <Text size="xs" c="dimmed" fs="italic" lineClamp={1} title={cliente.comentario || ''}>{cliente.comentario || '—'}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    {!(activeVuelo.clientes || []).length && (
                      <Table.Tr><Table.Td colSpan={4 + WEEKDAYS_FULL.length + 2} style={{ textAlign: 'center', padding: 20 }}><Text size="sm" c="dimmed">Sin clientes asignados</Text></Table.Td></Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Box>

            </Paper>
          ) : (
            <Paper shadow="xs" radius="md" p="xl"><Text c="dimmed" ta="center">Selecciona un vuelo</Text></Paper>
          )}
        </Grid.Col>
      </Grid>

      {/* ============================================================
          MODAL — Asignar / Editar Cliente
          ============================================================ */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={modalCliente ? 'Editar cliente' : 'Asignar cliente'} centered size="md">
        <Stack gap="md">
          <Select
            label="Cliente"
            placeholder="Seleccionar..."
            data={clienteSelectOptions}
            value={modalForm.clienteId}
            onChange={(v) => setModalForm({ ...modalForm, clienteId: v })}
            searchable
            disabled={!!modalCliente}
          />
          <Flex gap="md">
            <NumberInput label="Kg" value={modalForm.kg} onChange={(v) => setModalForm({ ...modalForm, kg: v || 0 })} min={0} step={50} style={{ flex: 1 }} onFocus={(e) => e.currentTarget.select()} />
            <NumberInput label="m³" value={modalForm.m3} onChange={(v) => setModalForm({ ...modalForm, m3: v || 0 })} min={0} step={0.5} decimalScale={1} style={{ flex: 1 }} onFocus={(e) => e.currentTarget.select()} />
            <NumberInput label="Prioridad" value={modalForm.prioridad} onChange={(v) => setModalForm({ ...modalForm, prioridad: v || 1 })} min={1} max={20} style={{ width: 90 }} />
          </Flex>
          <Select label="Servicio" placeholder="Seleccionar servicio..." data={catalogoServicios} value={modalForm.servicio || null} onChange={(v) => setModalForm({ ...modalForm, servicio: v || '' })} searchable clearable />
          <Box>
            <Text size="sm" fw={600} mb="xs">Días que opera</Text>
            <Flex gap="xs" wrap="wrap">
              {WEEKDAYS_FULL.map((dia) => (
                <Checkbox key={dia} label={dia} checked={modalForm.dias[dia] ?? false} onChange={() => setModalForm({ ...modalForm, dias: { ...modalForm.dias, [dia]: !modalForm.dias[dia] } })} color="#FA0073" />
              ))}
            </Flex>
          </Box>
          <TextInput label="Comentario" placeholder="Agregar comentario..." value={modalForm.comentario || ''} onChange={(e) => setModalForm({ ...modalForm, comentario: e.currentTarget.value })} maxLength={500} />
          <Flex justify="end" gap="sm" mt="sm">
            <Button variant="subtle" color="gray" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button color="#020C41" onClick={saveModal} disabled={!modalForm.clienteId} loading={saving}>Guardar</Button>
          </Flex>
        </Stack>
      </Modal>
    </>
  );
}
